import {
  ArrowsRightLeftIcon,
  CheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  SlashIcon,
} from "@heroicons/react/24/outline";
import React from "react";

import { ApiScraperLoginStatusChoices } from "@api/graphql/types-and-hooks";

export const EHRConnectBadge: React.FC<{
  status?: ApiScraperLoginStatusChoices | null;
  isDisconnecting?: boolean;
  isConnectionError?: boolean;
  /** Managed-onboarding gate: false until Frontera has cleared this tenant's
   * EHR connection, or the integration is graduated. Overrides every other
   * status below — an uncleared tenant should never see "disconnected" or
   * "failed" language for a connection Frontera hasn't finished setting up. */
  isEhrSyncCleared?: boolean;
}> = ({ status, isDisconnecting, isConnectionError, isEhrSyncCleared = true }) => {
  if (!isEhrSyncCleared) {
    return (
      <>
        <ClockIcon className="h-4 w-4 mr-1 text-text-primary" />
        Setting up your connection
      </>
    );
  }
  if (isDisconnecting) {
    return (
      <>
        <ArrowsRightLeftIcon className="h-4 w-4 mr-1 text-text-primary" />
        Disconnecting from EHR
      </>
    );
  }
  // Network/server error — not a credential failure, keep label distinct.
  if (isConnectionError) {
    return (
      <>
        <ExclamationTriangleIcon className="h-4 w-4 mr-1 text-red-500" />
        Failed EHR connection
      </>
    );
  }
  if (!status) {
    return (
      <>
        <SlashIcon className="h-4 w-4 mr-1 text-text-primary" />
        EHR disconnected
      </>
    );
  }
  if (status === ApiScraperLoginStatusChoices.LoggingIn) {
    return (
      <>
        <ArrowsRightLeftIcon className="h-4 w-4 mr-1 text-text-primary" />
        Connecting to EHR
      </>
    );
  }
  if (status === ApiScraperLoginStatusChoices.Success) {
    return (
      <>
        <CheckIcon className="h-4 w-4 mr-1 text-green-700" />
        EHR connected
      </>
    );
  }
  // Awaiting a code — actionable prompt, visually distinct from passive "Connecting".
  if (status === ApiScraperLoginStatusChoices.MfaRequired) {
    return (
      <>
        <ExclamationTriangleIcon className="h-4 w-4 mr-1 text-yellow-600" />
        Verification required
      </>
    );
  }
  // MFA code was rejected (terminal) — surface as a verification failure the user
  // retries. Credential failures / lockouts fall through to "Failed EHR connection"
  // below: "Verification failed" is reserved for the MFA path only.
  if (status === ApiScraperLoginStatusChoices.FailedMfa) {
    return (
      <>
        <ExclamationTriangleIcon className="h-4 w-4 mr-1 text-red-500" />
        Verification failed
      </>
    );
  }
  return (
    <>
      <ExclamationTriangleIcon className="h-4 w-4 mr-1 text-red-500" />
      Failed EHR connection
    </>
  );
};
